import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from '../css/modules/PricingOptions.module.css';
import { useCart } from '../components/CartContext';
import Faq from './Faq';
import JsonLd from '../seo/JsonLd';
import {
  productJsonLd,
  courseJsonLd,
  faqJsonLd,
  breadcrumbJsonLd,
  getPlans,
} from '../seo/siteMeta';
import { trackEvent, trackBeginCheckout } from '../lib/analytics';
import { formatPln } from '../lib/format';

const BASE_FEATURES = [
  'Pełna baza pytań egzaminacyjnych',
  'Wyjaśnienie do każdej odpowiedzi',
  'Tryb egzaminu na czas',
  'Powtórki błędnych odpowiedzi',
  'Dostęp na telefonie i komputerze',
];

const PRICING_FAQ = [
  {
    question: 'Czy to abonament? Czy dostęp odnowi się sam?',
    answer:
      'Nie. Płacisz jednorazowo za wybrany okres dostępu. Nic nie odnawia się automatycznie i nie pobieramy żadnych kolejnych opłat.',
  },
  {
    question: 'Kiedy dostanę dostęp do testów?',
    answer:
      'Od razu po zaksięgowaniu płatności. Konto zakładasz w trakcie zamówienia, więc wystarczy się zalogować i zacząć rozwiązywać pytania.',
  },
  {
    question: 'Jakie formy płatności akceptujecie?',
    answer:
      'Płatność obsługuje operator płatności online - zapłacisz szybkim przelewem bankowym, BLIK-iem lub kartą.',
  },
  {
    question: 'Który pakiet wybrać?',
    answer:
      'Jeśli egzamin masz za kilka dni, wystarczy krótki pakiet na intensywną powtórkę. Jeśli dopiero zaczynasz naukę albo planujesz zdawać na więcej niż jeden patent, wybierz najdłuższy pakiet - ma najniższy koszt dnia nauki.',
  },
  {
    question: 'Czy mogę sprawdzić testy przed zakupem?',
    answer:
      'Tak, wersja demo jest darmowa i nie wymaga rejestracji. Rozwiążesz w niej przykładowe pytania egzaminacyjne i ocenisz formę nauki.',
  },
  {
    question: 'Czy dostanę fakturę?',
    answer:
      'Tak. Napisz na kontakt@zeglarka.pl, podając adres e-mail użyty przy zamówieniu i dane do faktury.',
  },
];

const PricingOptions = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const options = useMemo(
    () =>
      getPlans().map(plan => {
        const dayLabel = plan.days === 1 ? 'dzień' : 'dni';
        const perDay = plan.days > 1 ? formatPln(plan.price / plan.days) : null;

        return {
          id: plan.id,
          days: plan.days,
          price: plan.price,
          name: `${plan.days} ${dayLabel} dostępu`,
          duration: plan.days.toString(),
          priceLabel: formatPln(plan.price),
          perDay,
          description: plan.desc,
        };
      }),
    []
  );

  const featuredId = options.length ? options[options.length - 1].id : null;

  const handlePurchase = option => {
    addToCart({
      id: option.id,
      name: option.name,
      duration: option.duration,
      price: option.priceLabel,
      description: option.description,
    });
    trackBeginCheckout({ value: option.price, days: option.days });
    trackEvent('cta_click', { location: 'pricing', label: `plan_${option.days}` });
    navigate(`/zakup?days=${option.duration}`);
  };

  return (
    <>
      <JsonLd
        data={[
          productJsonLd(),
          courseJsonLd(),
          faqJsonLd(PRICING_FAQ),
          breadcrumbJsonLd([
            { name: 'Strona główna', path: '/' },
            { name: 'Cennik', path: '/cennik' },
          ]),
        ]}
      />

      <main className={styles.page}>
        <section className={styles.hero}>
          <nav className={styles.breadcrumbs} aria-label="Ścieżka nawigacji">
            <Link to="/">Strona główna</Link> › <span>Cennik</span>
          </nav>

          <h1 className={styles.h1}>Cennik dostępu do testów na patent żeglarski</h1>

          <p className={styles.lead}>
            Wybierz, jak długo chcesz się uczyć. Płacisz raz, bez abonamentu i bez
            automatycznego przedłużania. Dostęp otrzymujesz od razu po opłaceniu zamówienia.
          </p>

          <ul className={styles.trustBar}>
            <li>Płatność jednorazowa</li>
            <li>Dostęp natychmiast po opłaceniu</li>
            <li>Bezpieczne płatności online</li>
            <li>Demo za darmo, bez rejestracji</li>
          </ul>
        </section>

        <section className={styles.plansSection} aria-label="Pakiety dostępu">
          <div className={styles.pricingGrid}>
            {options.map(option => {
              const featured = option.id === featuredId;
              const oldPrice =
                option.id === 2 ? process.env.REACT_APP_PLAN2_DISCOUNT_PRICE || '100 zł' : null;

              return (
                <article
                  key={option.id}
                  className={`${styles.pricingCard} ${featured ? styles.featured : ''}`}
                >
                  {featured && <span className={styles.badge}>Najczęściej wybierany</span>}

                  <h2 className={styles.planName}>{option.name}</h2>

                  <p className={styles.priceRow}>
                    {oldPrice && <span className={styles.oldPrice}>{oldPrice}</span>}
                    <span className={styles.price}>{option.price}</span>
                    <span className={styles.currency}>zł</span>
                  </p>

                  {option.perDay && (
                    <p className={styles.perDay}>to {option.perDay} za dzień nauki</p>
                  )}

                  <p className={styles.description}>{option.description}</p>

                  <ul className={styles.featureList}>
                    {BASE_FEATURES.map(feature => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>

                  <button
                    className={featured ? styles.buyButton : styles.buyButtonAlt}
                    onClick={() => handlePurchase(option)}
                    type="button"
                  >
                    Kupuję dostęp
                  </button>

                  <p className={styles.cardNote}>Bez subskrypcji · dostęp od razu</p>
                </article>
              );
            })}
          </div>

          <p className={styles.demoLine}>
            Nie jesteś pewien? <Link to="/egzamin/zeglarz-jachtowy">Rozwiąż najpierw darmowy egzamin próbny</Link> - bez
            rejestracji i bez opłat.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Co dokładnie dostajesz</h2>
          <div className={styles.prose}>
            <p>
              Dostęp obejmuje <strong>pełną bazę pytań</strong> na patent żeglarza jachtowego
              i sternika motorowodnego. Pytania są podzielone na siedem działów egzaminacyjnych:
              przepisy prawa drogi, budowę jachtu, manewrowanie, teorię żeglowania, locję
              i nawigację, ratownictwo i meteorologię.
            </p>
            <p>
              W bazie znajdziesz też pytania z arkuszy na jachtowego sternika morskiego, ale
              nie sprzedajemy tego jako przygotowania do patentu: ten stopień wymaga
              udokumentowanego stażu morskiego, którego żaden test nie zastąpi. Traktuj je
              jako materiał do samodzielnej nauki.
            </p>
            <p>
              Do tego symulator egzaminu: 75 pytań, 90 minut, próg 65 poprawnych odpowiedzi
              i wynik w rozbiciu na działy, wraz z listą błędów do powtórki. W trybie nauki
              wyjaśnienie i podstawa prawna pokazują się od razu po odpowiedzi.
            </p>
            <p>
              Zanim kupisz, możesz przejrzeć{' '}
              <Link to="/zeglarz-jachtowy">testy na patent żeglarza jachtowego</Link>{' '}
              albo rozwiązać{' '}
              <Link to="/egzamin/zeglarz-jachtowy">darmowy egzamin próbny</Link>.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Pytania o płatność i dostęp</h2>
          <Faq items={PRICING_FAQ} />
        </section>
      </main>
    </>
  );
};

export default PricingOptions;
